import { useQuery } from "@tanstack/react-query";

import {
  adminReportsExportsCreate,
  adminReportsExportsList,
  adminReportsOverview,
  getAdminReportsExportsFileUrl,
  getVendorReportsExportsFileUrl,
  vendorReportsExportsCreate,
  vendorReportsExportsList,
  vendorReportsOverview,
} from "@motolynk/api-client";
import { useTranslation } from "@motolynk/i18n";
import { formatMoney, KpiTile } from "@motolynk/ui";

import { lastDays, ReportsView, type ReportsSource } from "./ReportsView";

export const adminSource: ReportsSource = {
  panel: "admin",
  overview: async (params) => (await adminReportsOverview(params)).data,
  exportsList: async () => (await adminReportsExportsList()).data,
  exportsCreate: async (data) => (await adminReportsExportsCreate(data)).data,
  fileUrl: (uid) => getAdminReportsExportsFileUrl(uid),
  reports: ["orders", "order_items", "sales_by_day", "vendors"],
};

export const vendorSource: ReportsSource = {
  panel: "vendor",
  overview: async (params) =>
    (await vendorReportsOverview({ from: params.from, to: params.to })).data,
  exportsList: async () => (await vendorReportsExportsList()).data,
  exportsCreate: async (data) => (await vendorReportsExportsCreate(data)).data,
  fileUrl: (uid) => getVendorReportsExportsFileUrl(uid),
  reports: ["orders", "order_items", "sales_by_day"],
};

export function AdminReportsPage() {
  return <ReportsView source={adminSource} />;
}

export function VendorReportsPage() {
  return <ReportsView source={vendorSource} />;
}

/** The admin's store page gets a "Sales" tab with that store's figures (admin step 10: vendor list with sales). */
export function VendorSalesTab({ vendorUid }: { vendorUid: string }) {
  return <ReportsView source={adminSource} vendor={vendorUid} embedded />;
}

/** Dashboard tile: sales of the last 30 days (both panels). */
export function Sales30Tile({ panel }: { panel: "admin" | "vendor" }) {
  const { t, i18n } = useTranslation("reports");
  const range = lastDays(30);
  const source = panel === "admin" ? adminSource : vendorSource;
  const data = useQuery({
    queryKey: [panel, "reports", "overview", range],
    queryFn: () => source.overview(range),
  });
  return (
    <KpiTile
      label={t("tile.sales30")}
      value={data.data ? formatMoney(data.data.kpis.sales, "INR", i18n.language) : "—"}
      hint={data.data ? t("kpi.ordersHint", { count: data.data.kpis.orders }) : undefined}
      icon="trending"
    />
  );
}

export const AdminSales30Tile = () => <Sales30Tile panel="admin" />;
export const VendorSales30Tile = () => <Sales30Tile panel="vendor" />;
